import type { PixelClient } from "../client.js";
import { ActiveTimeTracker, attachActiveListeners } from "./active-time.js";
import { attachClickTracking } from "./clicks.js";
import { attachPageLeave } from "./page-leave.js";
import { pageContext, pageProps } from "./page-context.js";
import { attachProductEngagement, type ProductPayload } from "./product-engagement.js";
import { attachScrollDepth } from "./scroll-depth.js";
import { SHOPIFY_SELECTORS } from "./selectors.js";
import { startPageSession } from "./session.js";

/**
 * Config the Shopify app injects via the dynamic /loader.js response as window.RetargetConfig.
 * Prices must already be in minor units (the loader converts from Shopify's decimal price).
 */
export interface ShopifyProduct extends ProductPayload {}

export interface ShopifyCollection {
  collection_id: string;
  collection_title?: string;
  collection_type?: string;
}

export interface ShopifyPageSignals {
  product: ShopifyProduct | null;
  collection: ShopifyCollection | null;
  isCart: boolean;
  isCheckout: boolean;
  /** Path to the Shopify AJAX cart update endpoint. Default '/cart/update.js'. */
  cartUpdateUrl: string;
}

/**
 * Write the visitor id into Shopify cart note_attributes so the backend can tie an order_created
 * webhook back to the anonymous browsing session. Fire-and-forget; never throws.
 */
export async function stampCartVisitorId(visitorId: string, url: string): Promise<void> {
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note_attributes: { intent_visitor_id: visitorId } }),
    });
  } catch {
    // Storefront stability over telemetry — a cart stamp failure is silent.
  }
}

/**
 * Wire DOM + Shopify events to the client. Analogous to attachWooCommerce but for Shopify
 * storefronts. Stamps intent_visitor_id in cart note_attributes for order webhook stitching.
 */
export function attachShopify(client: PixelClient, signals: ShopifyPageSignals): () => void {
  const cleanups: Array<() => void> = [];
  const visitorId = client.visitorId();
  const activeTime = new ActiveTimeTracker();

  cleanups.push(attachActiveListeners(activeTime));
  cleanups.push(attachScrollDepth(client, () => activeTime.markActive()));
  cleanups.push(attachClickTracking(client, signals.product?.product_id));
  cleanups.push(attachPageLeave(client, activeTime));

  startPageSession(client);
  void client.track("pageview", pageProps(), pageContext());

  if (signals.collection) {
    void client.track("collection_view", { ...signals.collection }, pageContext());
  }

  if (signals.product) {
    void client.track("product_view", { ...signals.product }, pageContext());
    cleanups.push(attachProductEngagement(client, signals.product, SHOPIFY_SELECTORS, activeTime));
  }

  if (signals.isCart) {
    void client.track("cart_view", {}, pageContext());
  }
  if (signals.isCheckout) {
    void client.track("checkout_start", {}, pageContext());
  }

  void stampCartVisitorId(visitorId, signals.cartUpdateUrl);

  const onSubmit = (e: Event) => {
    const form = (e.target as HTMLElement | null)?.closest?.('form[action*="/cart/add"]');
    if (!form) return;
    const props = signals.product ? { ...signals.product } : {};
    void client.track("add_to_cart", props, pageContext());
    void stampCartVisitorId(visitorId, signals.cartUpdateUrl);
  };
  document.addEventListener("submit", onSubmit, true);
  cleanups.push(() => document.removeEventListener("submit", onSubmit, true));

  const onAjaxAdd = () => {
    const props = signals.product ? { ...signals.product } : {};
    void client.track("add_to_cart", props, pageContext());
    void stampCartVisitorId(visitorId, signals.cartUpdateUrl);
  };
  document.body.addEventListener("cart:item-added", onAjaxAdd as EventListener);
  cleanups.push(() =>
    document.body.removeEventListener("cart:item-added", onAjaxAdd as EventListener),
  );

  return () => {
    for (const fn of cleanups) fn();
  };
}
